import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { useUpdateProfileInfoMutation } from '../../../Account/api';
import { setProfile } from '../../../Account/state/profileSlice';
import { setCheckoutInfo } from '../../../domains/Checkout/slice';
import { CheckoutInfoPayload } from '../../../domains/Checkout';

import { Button } from '../../../GlobalComponents/Button';
import { Checkbox } from '../../../GlobalComponents/Checkbox';
import { ErrorMessage } from '../../../GlobalComponents/ErrorMessage';
import { Flex } from '../../../GlobalComponents/Flex';
import { Form } from '../../../GlobalComponents/Form';
import { Input } from '../../../GlobalComponents/Input';
import { InputTextMask } from '../../../GlobalComponents/InputMask';
import { useAppDispatch, useAppSelector } from '../../../reduxstore/hooks';
import { Heading } from '../../components/Heading';

interface ProfileForm {
	firstName: string;
	phone: string;
	email: string;
}

export const Profile: React.FC = () => {
	const [sync, setSync] = useState<boolean>(false);
	const userId = useAppSelector(state => state.authReducer.id);
	const profileData = useAppSelector(state => state.accountReducer);
	const authenticated = useAppSelector(state => state.authReducer.accessToken)
		? true
		: false;
	const checkoutState = useAppSelector(state => state.checkoutReducer);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [updateProfileInfo, { isError, isLoading, error }] =
		useUpdateProfileInfoMutation();

	const [formData, setFormData] = useState<ProfileForm>({
		firstName: '',
		email: '',
		phone: ''
	});
	const [formError, setFormError] = useState({
		name: false,
		email: false,
		phone: false
	});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (formError.email || formError.phone || formError.name) {
			setFormError({ name: false, email: false, phone: false });
		}
		let value = e.target.value;

		if (e.target.name === 'firstName') {
			value = value.replace(/[^a-zA-Z\s]/g, '');
		}

		if (e.target.name === 'email') {
			value = value.replace(' ', '');
		}

		if (formData) {
			setFormData({
				...formData,
				[e.target.name]: value
			});
		}
	};

	const handlePhoneChanges = (value: string) => {
		if (formError.email || formError.phone || formError.name) {
			setFormError({ name: false, email: false, phone: false });
		}
		setFormData({
			...formData,
			phone: value
		});
	};

	const handleContinue = () => {
		let phoneError = false;
		let emailError = false;
		let nameError = false;

		const { email, firstName, phone } = formData;

		if (isEmpty(firstName)) {
			nameError = true;
		}
		if (!isEmail(email) || isEmpty(email)) {
			emailError = true;
		}

		if (isEmpty(`${phone}`) || !isMobilePhone(`${phone}`, 'en-US')) {
			phoneError = true;
		}

		if (phoneError || emailError || nameError) {
			setFormError({
				name: nameError,
				email: emailError,
				phone: phoneError
			});
			return;
		}

		if (authenticated && sync) {
			updateProfileInfo({
				...profileData.profile,
				first_name: formData.firstName,
				phone: formData.phone,
				id: userId
			})
				.unwrap()
				.then((res: ProfileResponse) => {
					dispatch(setProfile(res.data.profile));
				});
		}

		dispatch(
			setCheckoutInfo({
				...formData,
				touched: true
			} as CheckoutInfoPayload)
		);

		navigate('../pickup');
	};

	const onSyncChange = () => {
		setSync(!sync);
	};

	useEffect(() => {
		const { touched, firstName, email, phone } = checkoutState;
		if (authenticated && touched === false) {
			setFormData({
				firstName: profileData.profile.first_name,
				email: profileData.profile.email as string,
				phone: profileData.profile.phone as string
			});
		}
		if (touched) {
			setFormData({
				firstName: firstName ? firstName : '',
				phone: phone ? phone : '',
				email: email ? email : ''
			});
		}
	}, [authenticated, profileData, checkoutState]);

	return (
		<Flex $flexDirection="column" $rowGap="16px">
			<Heading $fontSize="24px">Who's picking this up?</Heading>
			<div className={`checkout`}>
				{formError.email ||
					formError.name ||
					(formError.phone && (
						<ErrorMessage
							message={
								'Please check that all fields are filled out correctly.'
							}
						/>
					))}
				{isError && <ErrorMessage error={error} />}
				<Form $noColumn $maxWidth="100%">
					<Input
						label="First Name"
						name="firstName"
						value={formData.firstName}
						placeholder="First Name"
						onChange={handleChange}
						$error={formError.name ? 'Please enter a name' : null}
					/>

					<Input
						label="Email"
						name="email"
						value={formData.email}
						placeholder="Email"
						onChange={handleChange}
						$error={
							formError.email
								? 'Please enter a valid email'
								: null
						}
					/>
					<InputTextMask
						name="phone"
						label="Phone"
						type="tel"
						inputmode="tel"
						placeholder="Phone Number"
						value={formData.phone}
						onChange={handlePhoneChanges}
						$error={
							formError.phone
								? 'Please enter a valid mobile phone'
								: null
						}
					/>
				</Form>

				<Flex
					$flexDirection="column"
					$rowGap="16px"
					$margin="24px 0 0 0"
				>
					{authenticated && (
						<Checkbox
							name="syncData"
							label={'Save name and phone to my profile'}
							isChecked={sync}
							handleChange={onSyncChange}
						/>
					)}
					<Button
						$width="100%"
						$border="2px solid #574D45"
						$backgroundColor="#F5F2F2"
						$color="#574D45"
						onClick={handleContinue}
						$isLoading={isLoading}
					>
						Continue
					</Button>
				</Flex>
			</div>
		</Flex>
	);
};
